import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import { PerspectiveCamera, useGLTF } from '@react-three/drei'
import { GroupProps, useFrame, useThree } from '@react-three/fiber'
import { useGesture } from '@use-gesture/react'
import gsap from 'gsap'
import { forEach } from 'lodash'
import {
  AnimationAction,
  AnimationMixer,
  DirectionalLight,
  Group,
  MathUtils,
  Object3D,
  Vector3
} from 'three'

import usePageVisibility from '@/hooks/usePageVisibility'
import { useRouterStore } from '@/store/router'
import Shapes from '@/webgl/components/Shapes'
import Steps from '@/webgl/components/Steps'
import { SPLINE_PATH } from '@/webgl/constants'

import { useLenisStore } from '@/contexts/lenis'
import { TRANSITION_ENTER_DURATION } from '@/contexts/transition'

const Spline = forwardRef<Group, GroupProps>(({ children, ...props }, ref) => {
  const { nodes, materials, animations } = useGLTF(SPLINE_PATH) as any

  const root = useRef<Group>(null!)
  const light = useRef<DirectionalLight>(null!)
  const target = useRef<Object3D>(null!)
  const mixer = useRef<AnimationMixer>(null!)
  const cameraAnimation = useRef<AnimationAction>(null!)
  const progress = useRef(0)

  const [center] = useState(() => new Vector3())
  const [pointer] = useState(() => new Vector3())

  const clock = useThree((state) => state.clock)
  const documentHidden = usePageVisibility()

  useEffect(
    () => (documentHidden ? clock.stop() : clock.start()),
    [clock, documentHidden]
  )

  const setupAnimations = useCallback(() => {
    mixer.current = new AnimationMixer(root.current)

    let actionDelay = 0
    forEach(animations, (clip) => {
      if (clip.name === 'camera-anim') {
        cameraAnimation.current = mixer.current.clipAction(clip, root.current)
        cameraAnimation.current.play().paused = true
      } else if (clip.name === 'extra-shape-anim') {
        const action = mixer.current.clipAction(clip, root.current)
        action.startAt(actionDelay % 3).play()
        ++actionDelay
      } else {
        for (let i = 1; i <= 5; i++) {
          for (let j = 1; j <= 3; j++) {
            const name = `step-${i}-anim-${j}`
            if (name === clip.name) {
              const action = mixer.current.clipAction(clip, root.current)
              action.play()
            }
          }
        }
      }
    })
  }, [animations])

  const updateCameraAnimation = useCallback((delta: number) => {
    progress.current = MathUtils.lerp(
      progress.current,
      useLenisStore.getState().progress,
      delta * 3
    )
    cameraAnimation.current.time =
      cameraAnimation.current.getClip().duration * progress.current
  }, [])

  const resetCameraAnimation = useCallback(() => {
    useLenisStore.getState().setState({ progress: 0 })
    progress.current = 0
  }, [])

  const updateLightTarget = useCallback(() => {
    light.current.target = target.current
  }, [])

  const updateCameraPosition = useCallback(
    (camera: any, delta: number) => {
      target.current.getWorldPosition(center)

      camera.position.x = MathUtils.lerp(
        camera.position.x,
        -pointer.x * 8,
        delta * 2
      )
      camera.position.y = MathUtils.lerp(
        camera.position.y,
        pointer.y * 8,
        delta * 2
      )
      camera.lookAt(center)
    },
    [center, pointer]
  )

  useFrame(({ camera }, delta) => {
    if (!mixer.current) return
    mixer.current.update(delta)

    updateCameraPosition(camera, delta)
    updateCameraAnimation(delta)
    updateLightTarget()
  })

  useGesture(
    {
      onMove: ({ xy: [x, y] }) => {
        const mouseX = (x / window.innerWidth) * 2 - 1
        const mouseY = -(y / window.innerHeight) * 2 + 1
        pointer.set(mouseX, mouseY, 0)
      }
    },
    {
      target: document.body
    }
  )

  useIsomorphicLayoutEffect(() => {
    const subscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      (route) => {
        if (route !== '/') return
        gsap.delayedCall(TRANSITION_ENTER_DURATION, () => {
          resetCameraAnimation()
        })
      },
      { fireImmediately: true }
    )

    return () => {
      subscribe()
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    setupAnimations()
  }, [])

  return (
    <group ref={ref as any} {...props}>
      <group ref={root as any} dispose={null}>
        <group name="camera-group-anim">
          <group ref={target as any} name="camera-target" />
          <PerspectiveCamera
            name="camera-anim"
            makeDefault={true}
            far={1000}
            near={0.1}
            fov={23.994}
            position={[0, 0, 29.833]}
          >
            {children}
          </PerspectiveCamera>
          <directionalLight
            ref={light as any}
            intensity={2}
            color="#ffffff"
            position={[23.361, 0, 13.652]}
            rotation={[Math.PI / 2, 0, -1.042]}
          />
        </group>

        <mesh
          name="main-spline"
          castShadow
          receiveShadow
          geometry={nodes['main-spline'].geometry}
          material={materials['white-flat']}
          position={[0, -24.601, 0]}
          rotation={[-Math.PI, 1.373, -Math.PI]}
        />
        <mesh
          name="virvelle-logo"
          castShadow
          receiveShadow
          geometry={nodes['virvelle-logo'].geometry}
          material={materials.logo}
          material-color="#9DA6AD"
          material-emissive="#C4D0D9"
          material-emissiveIntensity={1}
          material-roughness={1}
          material-metalness={0}
          position={[0, -299.636, 0]}
        />
        <mesh
          name="step-1-torus"
          castShadow
          receiveShadow
          geometry={nodes['step-1-torus'].geometry}
          material={materials['green-flat']}
          position={[0.804, -50.219, -0.044]}
          rotation={[-1.225, 0.268, -1.203]}
          scale={1.489}
        />

        <Steps nodes={nodes} materials={materials} />
        <Shapes nodes={nodes} materials={materials} />
      </group>
    </group>
  )
})

// useGLTF.preload(SPLINE_PATH)

export default Spline
